import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Globe2, Languages, Save } from 'lucide-react';
import { Button } from '../../common';
import { SelectDropdown, type SelectOption } from '../../common';
import { useUserStore } from '@auw/stores';
import { getUserLocale } from '@auw/workspace-utils';
import {
  getProfileCountryOptions,
  getProfileLanguageOptions,
  getCountryCodeFromLanguage,
  getDefaultLanguageForCountry,
  normalizeProfileCountryCode,
  normalizeProfileLanguageCode,
} from '@auw/i18n';
import {
  ActionRow,
  Divider,
  ErrorMsg,
  SectionBlock,
  SectionDesc,
  SectionHeader,
  SectionTitle,
} from '../styles';

const LocaleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const LocaleCard = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px;
  border-radius: 14px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  background: ${({ theme }) => theme.colors.bgSecondary};
  transition: border-color ${({ theme }) => theme.transitions.fast},
    box-shadow ${({ theme }) => theme.transitions.fast},
    transform ${({ theme }) => theme.transitions.fast};

  &:hover {
    transform: translateY(-1px);
    border-color: ${({ theme }) => theme.colors.border};
    box-shadow: ${({ theme }) => theme.shadows.sm};
  }

  &:has([data-open='true']) {
    z-index: 20;
  }
`;

const LocaleCardHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const LocaleIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.accent};
  background: ${({ theme }) => theme.colors.accentLight};
`;

const LocaleMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const LocaleLabel = styled.span`
  font-size: 0.8125rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const LocaleHint = styled.span`
  font-size: 0.75rem;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const LocaleSelect = styled(SelectDropdown)`
  width: 100%;

  button[aria-haspopup='listbox'] {
    min-height: 48px;
    padding: 0 14px;
    border-radius: 12px;
    font-size: 0.875rem;
  }

  [role='listbox'] {
    margin-top: 6px;
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(32, 32, 34, 0.98)' : 'rgba(255, 255, 255, 0.98)'};
  }
`;

export const LanguageSection: React.FC = () => {
  const { t, i18n } = useTranslation('settings');
  const user = useUserStore((s) => s.user);
  const updateProfile = useUserStore((s) => s.updateProfile);

  const browserLocale = useMemo(() => getUserLocale(), []);
  const languageOptions = useMemo<SelectOption[]>(
    () =>
      getProfileLanguageOptions().map((option) => ({
        value: option.value,
        label: option.label,
        description: option.description,
      })),
    [i18n.language],
  );
  const countryOptions = useMemo<SelectOption[]>(
    () =>
      getProfileCountryOptions().map((option) => ({
        value: option.value,
        label: option.label,
        description: option.description,
      })),
    [i18n.language],
  );

  const resolvedInitialLanguage = useMemo(
    () => normalizeProfileLanguageCode(user?.preferredLanguage ?? browserLocale.language),
    [browserLocale.language, user?.preferredLanguage],
  );
  const resolvedInitialCountry = useMemo(() => {
    return normalizeProfileCountryCode(
      user?.countryCode
      ?? getCountryCodeFromLanguage(user?.preferredLanguage)
      ?? browserLocale.country_code,
    );
  }, [browserLocale.country_code, user?.countryCode, user?.preferredLanguage]);
  const persistedLanguage = useMemo(
    () => (user?.preferredLanguage ? normalizeProfileLanguageCode(user.preferredLanguage) : ''),
    [user?.preferredLanguage],
  );
  const persistedCountry = useMemo(
    () => normalizeProfileCountryCode(user?.countryCode),
    [user?.countryCode],
  );

  const [preferredLanguage, setPreferredLanguage] = useState(resolvedInitialLanguage);
  const [countryCode, setCountryCode] = useState(resolvedInitialCountry);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setPreferredLanguage(resolvedInitialLanguage);
    setCountryCode(resolvedInitialCountry);
    setError(null);
  }, [resolvedInitialCountry, resolvedInitialLanguage, user?.userId]);

  const isDirty = preferredLanguage !== persistedLanguage || countryCode !== persistedCountry;

  const handleCountryChange = (value: string) => {
    const nextCountryCode = normalizeProfileCountryCode(value);
    setCountryCode(nextCountryCode);
    const nextLanguage = getDefaultLanguageForCountry(nextCountryCode);
    if (nextLanguage) {
      setPreferredLanguage(nextLanguage);
    }
    setError(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await updateProfile({
        preferred_language: preferredLanguage,
        country_code: countryCode || null,
      });
    } catch {
      setError(t('settings:language.saveFail'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionBlock>
      <SectionHeader>
        <SectionTitle>{t('settings:language.title')}</SectionTitle>
      </SectionHeader>
      <SectionDesc>{t('settings:language.description')}</SectionDesc>
      <Divider />

      <LocaleGrid>
        <LocaleCard>
          <LocaleCardHead>
            <LocaleIcon>
              <Globe2 size={18} />
            </LocaleIcon>
            <LocaleMeta>
              <LocaleLabel>{t('settings:language.countryLabel')}</LocaleLabel>
            </LocaleMeta>
          </LocaleCardHead>
          <LocaleSelect
            options={countryOptions}
            value={countryCode}
            onChange={handleCountryChange}
            placeholder={t('settings:language.countryPlaceholder')}
            searchable
            searchPlaceholder={t('settings:language.countrySearch')}
            emptyMessage={t('common:search.noResults')}
          />
        </LocaleCard>

        <LocaleCard>
          <LocaleCardHead>
            <LocaleIcon>
              <Languages size={18} />
            </LocaleIcon>
            <LocaleMeta>
              <LocaleLabel>{t('settings:language.languageLabel')}</LocaleLabel>
            </LocaleMeta>
          </LocaleCardHead>
          <LocaleSelect
            options={languageOptions}
            value={preferredLanguage}
            onChange={(value) => {
              setPreferredLanguage(normalizeProfileLanguageCode(value));
              setError(null);
            }}
            placeholder={t('settings:language.languagePlaceholder')}
            searchable
            searchPlaceholder={t('settings:language.languageSearch')}
            emptyMessage={t('common:search.noResults')}
          />
        </LocaleCard>
      </LocaleGrid>

      {error && <ErrorMsg>{error}</ErrorMsg>}

      <ActionRow>
        <Button
          variant="primary"
          size="sm"
          leftIcon={<Save size={14} />}
          onClick={handleSave}
          disabled={!isDirty || saving}
        >
          {saving ? t('common:button.saving') : t('settings:language.saveButton')}
        </Button>
      </ActionRow>
    </SectionBlock>
  );
};
